/**
 * TagsField — search-UX §3.11.
 *
 * Controlled multi-select tag picker.  The value is a
 * comma-separated string (matching the wire shape `item.tags`); the
 * component splits, dedupes case-insensitively, renders selected
 * chips, and shows a typeahead dropdown filtered by the in-progress
 * query.
 *
 * Mirrors `src/features/browse/components/TagTypeahead.tsx` (which
 * is Redux-bound for the Browse Tag layout) but is a CONTROLLED
 * component for form-state callers like EditItemSheet.  One UX
 * pattern, two callsites.
 *
 * Closes the duplicate-tag fragmentation that today makes Browse
 * Tag layout's grouping noisy ("Kitchen" vs "kitchen" vs "kit").
 * Notion's multi-select picker is the precedent.
 */

import React, { useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

export interface TagsFieldProps {
  /** Form-state value as a comma-separated string.  Empty string =
   *  no tags. */
  value: string;
  /** Fires with the next comma-separated string. */
  onChange: (next: string) => void;
  /** Available tags (typically from `selectAllTags(state)`). */
  suggestions: Array<{ tag: string; count: number }>;
  /** Field label (above the chips). */
  label?: string;
  /** Input placeholder. */
  placeholder?: string;
  /** Optional testID prefix. */
  testID?: string;
}

const MAX_DROPDOWN_ROWS = 8;

export const TagsField: React.FC<TagsFieldProps> = ({
  value,
  onChange,
  suggestions,
  label = 'Tags',
  placeholder = 'Add tag',
  testID,
}) => {
  // Parse the comma-separated value into a deduped list of trimmed
  // tag strings.  Keep the original casing for display.
  const selected = useMemo<string[]>(() => {
    return value
      .split(/[,;]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }, [value]);

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);

  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const alreadyPicked = new Set(selected.map((t) => t.toLowerCase()));
    const pool = suggestions.filter(
      (t) => !alreadyPicked.has(t.tag.toLowerCase()),
    );
    if (!q) return pool.slice(0, MAX_DROPDOWN_ROWS);
    return pool
      .filter((t) => t.tag.toLowerCase().includes(q))
      .slice(0, MAX_DROPDOWN_ROWS);
  }, [query, selected, suggestions]);

  const showCreate =
    query.trim().length > 0 &&
    !filteredSuggestions.some(
      (t) => t.tag.toLowerCase() === query.trim().toLowerCase(),
    ) &&
    !selected.some(
      (s) => s.toLowerCase() === query.trim().toLowerCase(),
    );

  const addTag = (tag: string): void => {
    const t = tag.trim();
    if (!t) return;
    // Case-insensitive dedupe vs already selected.
    if (selected.some((s) => s.toLowerCase() === t.toLowerCase())) return;
    onChange([...selected, t].join(', '));
    setQuery('');
  };

  const removeTag = (tag: string): void => {
    onChange(
      selected.filter((s) => s.toLowerCase() !== tag.toLowerCase()).join(', '),
    );
  };

  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>

      {selected.length > 0 && (
        <View style={styles.chipRow}>
          {selected.map((tag) => (
            <Pressable
              key={`sel-${tag}`}
              onPress={() => removeTag(tag)}
              style={({ pressed }) => [
                styles.selectedChip,
                pressed && { opacity: 0.7 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Remove tag ${tag}`}
              testID={testID ? `${testID}-chip-${tag}` : undefined}
            >
              <Text style={styles.selectedChipLabel}>{tag}</Text>
              <MaterialIcons
                name="close"
                size={12}
                color={fpColors.onPrimary}
                style={styles.selectedChipX}
              />
            </Pressable>
          ))}
        </View>
      )}

      <View style={styles.inputWrap}>
        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setTimeout(() => setFocused(false), 120);
          }}
          placeholder={placeholder}
          placeholderTextColor={`${fpColors.outline}99`}
          autoCorrect={false}
          autoCapitalize="none"
          style={styles.input}
          onSubmitEditing={() => {
            if (filteredSuggestions.length > 0)
              addTag(filteredSuggestions[0].tag);
            else if (showCreate) addTag(query.trim());
          }}
          testID={testID ? `${testID}-input` : undefined}
        />
      </View>

      {focused && (filteredSuggestions.length > 0 || showCreate) && (
        <View
          style={styles.dropdown}
          testID={testID ? `${testID}-dropdown` : undefined}
        >
          <FlatList
            data={filteredSuggestions}
            keyboardShouldPersistTaps="handled"
            keyExtractor={(item) => `sug-${item.tag}`}
            renderItem={({ item }) => (
              <Pressable
                onPress={() => addTag(item.tag)}
                style={({ pressed }) => [
                  styles.row,
                  pressed && styles.rowPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Add tag ${item.tag}`}
                testID={testID ? `${testID}-row-${item.tag}` : undefined}
              >
                <Text style={styles.rowLabel} numberOfLines={1}>
                  {item.tag}
                </Text>
                <Text style={styles.rowCount}>
                  {item.count === 1 ? '1 item' : `${item.count} items`}
                </Text>
              </Pressable>
            )}
            ListFooterComponent={
              showCreate ? (
                <Pressable
                  onPress={() => addTag(query.trim())}
                  style={({ pressed }) => [
                    styles.row,
                    styles.createRow,
                    pressed && styles.rowPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Create tag ${query.trim()}`}
                  testID={testID ? `${testID}-create` : undefined}
                >
                  <MaterialIcons
                    name="add"
                    size={16}
                    color={fpColors.primary}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={styles.createLabel} numberOfLines={1}>
                    {`Create tag "${query.trim()}"`}
                  </Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  field: { marginBottom: 12 },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurfaceVariant,
    marginBottom: 4,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 6,
  },
  selectedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
  },
  selectedChipLabel: {
    color: fpColors.onPrimary,
    fontSize: 12,
    fontWeight: '600',
  },
  selectedChipX: { marginLeft: 4 },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  dropdown: {
    marginTop: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    maxHeight: 240,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
  },
  rowCount: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  createRow: { borderBottomWidth: 0 },
  createLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.primary,
  },
});

export default TagsField;
